import React, {
  FC,
  ReactNode,
  SyntheticEvent,
  useEffect,
  useState,
} from 'react'

import { useDispatch } from 'react-redux'

import {
  fetchComplaintListAction,
  saveComplaintAction,
} from 'actions/complaints/complaintsAction'
import { removeListComplainAction } from 'actions/complaints/complaintsStreamActions'
import { hideTooltipsAction } from 'actions/tooltipAction'
import {
  ComplaintsEntityType,
  ComplaintsType,
  ProfileId,
} from 'api/complaints/complaintsApi'
import { VoidHandler } from 'common/types'
import { useRedesignLayout } from 'hooks/useRedesign'
import { useReducersInsert } from 'hooks/useReducersInsert'
import { useShallowEqualSelector } from 'hooks/useShallowEqualSelector'
import { complaintReducer } from 'reducers/complaintReducer'

import { ComplaintListNextView } from './ComplaintListNextView'
import { ComplaintListView } from './ComplaintListView'

/**
 *
 * @param complaintListType - тип жалоб при запросе на получение списка жалоб
 * @param violatorId - id нарушителя
 * @param complaintType - тип сущности на которую жалуются при отправке на сервер
 * @param entityId - id сущности(профиль, фото, комментарий и т.д.) нарушителя.
 * @param actionAfterComplainSend - callback после отправки
 * @param onlyRealComplaints - показываем только реальные жалобы сразу
 * @param deleteContact - пункт удаление контакта(только для чата)
 * @param muteContact - пункт выключение уведомления от пользователя(только для чата)
 * @param clearChat - пункт очистка чата (только для чата)
 * @param closeComplains - пункт закрытие жалоб внутри tooltip жалоб
 * @constructor
 */
export const ComplainList: FC<{
  complaintListType: ComplaintsType
  violatorId: ProfileId
  complaintType: ComplaintsType
  entityId: ComplaintsEntityType
  actionAfterComplainSend?: VoidHandler
  onlyRealComplaints?: boolean
  deleteContact?: ReactNode
  photoVisibilityToggle?: ReactNode
  muteContact?: ReactNode
  clearChat?: ReactNode
  closeComplains?: boolean
  showRedesignComplaints?: boolean
}> = ({
  complaintListType,
  violatorId,
  complaintType,
  entityId,
  actionAfterComplainSend,
  onlyRealComplaints = false,
  deleteContact,
  photoVisibilityToggle,
  muteContact,
  clearChat,
  closeComplains,
  showRedesignComplaints = false,
}) => {
  useReducersInsert({ complaintReducer })
  const dispatch = useDispatch()
  const redesignLayout = useRedesignLayout()
  const [showRealComplaints, setShowRealComplaints] =
    useState<boolean>(onlyRealComplaints)

  const { complaintListReal, complaintListNotReal } = useShallowEqualSelector(
    ({ complaintReducer: { complaintListReal, complaintListNotReal } }) => ({
      complaintListReal,
      complaintListNotReal,
    })
  )
  const handleClickSendComplaint = (causeId: number) => {
    dispatch(
      saveComplaintAction(
        violatorId,
        complaintType,
        causeId,
        entityId,
        actionAfterComplainSend
      )
    )
  }

  const handleShowRealComplaintsToggle = (event: SyntheticEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setShowRealComplaints((showRealComplaints) => !showRealComplaints)
  }

  const handleClose = () => {
    dispatch(hideTooltipsAction())
  }

  useEffect(() => {
    dispatch(fetchComplaintListAction(violatorId, complaintListType, entityId))

    return () => {
      dispatch(removeListComplainAction())
    }
  }, [dispatch, complaintListType, entityId, violatorId])

  if (complaintListReal?.length > 0) {
    if (redesignLayout && showRedesignComplaints) {
      return (
        <ComplaintListNextView
          complaintListNotReal={complaintListNotReal}
          complaintListReal={complaintListReal}
          showRealComplaints={showRealComplaints}
          onShowRealComplaintsToggle={handleShowRealComplaintsToggle}
          onClickSendComplaint={handleClickSendComplaint}
        />
      )
    }

    return (
      <ComplaintListView
        showRealComplaints={showRealComplaints}
        complaintListNotReal={complaintListNotReal}
        complaintListReal={complaintListReal}
        muteContact={muteContact}
        deleteContact={deleteContact}
        onClickSendComplaint={handleClickSendComplaint}
        onShowRealComplaintsToggle={handleShowRealComplaintsToggle}
        onHandleClose={handleClose}
        closeComplains={closeComplains}
        clearChat={clearChat}
        photoVisibilityToggle={photoVisibilityToggle}
        onlyRealComplaints={onlyRealComplaints}
      />
    )
  }
  return null
}
